<template>
  <v-row>
    <v-col>
      <v-card elevation="0">
        <v-card-title>
          <div>
            <h1
              style="font-size: 35px"
              class="md-header-no-spacing"
              data-testid="company-page-title"
            >
              Company
            </h1>
          </div>
          <v-spacer />
          <v-progress-circular
            v-show="loading"
            :active="true"
            :indeterminate="true"
            size="25"
            width="2"
            class="mr-2"
          />
        </v-card-title>
        <v-progress-linear
          :active="distributorPending"
          :indeterminate="true"
          class="ma-0"
          height="4"
          style="top: -2px"
        >
          Loading...
        </v-progress-linear>

        <v-card-text v-if="!distributorPending">
          <p class="mb-2" data-testid="company-page-purpose">
            Who your business is, and where to reach and bill it.
          </p>
          <company-form
            :distributor="distributor"
            :readonly="!hasAccess"
            :ein-distributor-id="showEin ? einDistributorId : null"
            :save="saveRecord"
            @update:field="setField"
          />
        </v-card-text>
      </v-card>
    </v-col>
    <validate-address-dialog
      ref="validateAddressDialog"
      :originalLocation="distributor.address"
      @useValidatedAddress="updateAddressToValidated"
      @useOriginalAddress="submitHandler(redirectToHome)"
    />
  </v-row>
</template>

<script>
// PR-8 (frontend-rearchitecture, role-sweep-tier-1):
// Permission gates migrated to capability-based `can('...')` via
// usePermissions. The remaining `roleName()` computed and the
// `setLoadingVariable` role-name switch are STORAGE / DISPATCH logic
// (not permission gates) — they choose which distributor id to load
// based on the viewer's role enum.
//
// Impersonation fix (2026-06-07): setLoadingVariable now prefers the
// Pinia account store's `distributor.id` over the route/role-enum
// dispatch. That makes the Owner path and the SA-impersonating path
// converge on the same source of truth (the effective tenant for the
// session), instead of the role-enum switch silently dropping the
// SA-impersonating case on /distributor/account/company (the company
// profile; it was at /distributor/profile until the 2026-08-11 route
// rename) where there is no :id route param. See setLoadingVariable()
// for the full dispatch order.
import { nexusStateName } from "@/data/usStates";
import {
  isUnparseableAddress,
  unparseableAddressMessage,
} from "@/utils/addressParse";
import Distributor from "@/models/Distributor";
import rules from "@/mixins/rules";
import { mapGetters } from "vuex";
import helpers from "@/mixins/helpers";
import ValidateAddressDialog from "@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue";
import CompanyForm from "./CompanyForm.vue";
import { COMPANY_FIELDS } from "./companyFields";
import { usePermissions } from "@/composables/usePermissions";
import { useAccountStore } from "@/stores/account";

export default {
  setup() {
    const { can, cannot } = usePermissions();
    // PR (impersonation fix): the Pinia `account` store carries the
    // *effective* tenant for the current session -- the impersonated
    // distributor when an SA is impersonating, the Owner's own tenant
    // otherwise. setLoadingVariable() reads `accountStore.distributor.id`
    // to pick the right tenant to load. Impersonation start/stop both
    // trigger a full page reload today (window.location.reload /
    // window.location.assign), so reactive re-evaluation is not required;
    // reading from the store keeps us consistent with other surfaces and
    // avoids snapshot-then-stale bugs if the reload-on-impersonate
    // behavior is ever relaxed.
    const accountStore = useAccountStore();
    return {
      can,
      cannot,
      accountStore,
    };
  },

  components: {
    "validate-address-dialog": ValidateAddressDialog,
    CompanyForm,
  },
  data() {
    return {
      distributor: Distributor.generateEmpty(),

      distributorPending: true,

      // Home state of a distributor being CREATED here (validated address).
      // Seeded into nexus after create, not editable on this page; the one
      // nexus editor is Account > Compliance.
      newDistributorHomeState: null,
      loadingVariable: null,
      // "updated" | "created", set by persistRecord for the success toast.
      savedVerb: null,

      addressChanged: false,
      homeState: null,
      loading: false,

      pagination: {
        sortBy: "active",
        rowsPerPage: 20,
        descending: true,
      },
    };
  },

  watch: {
    roleName: {
      handler() {
        this.setLoadingVariable(this.roleName);
        this.loadData();
      },
    },
  },

  computed: {
    ...mapGetters(["getStates", "getCountries"]),

    // The EIN is Owner-level (the API requires distributor.account.write);
    // it is only offered for an existing distributor record.
    showEin() {
      return (
        this.can("distributor.account.write") &&
        !!(this.distributor._jv && this.distributor._jv.id) &&
        !this.distributorPending
      );
    },

    einDistributorId() {
      return this.distributor._jv ? this.distributor._jv.id : null;
    },

    isDistributorManager() {
      // Capability gate: DM has distributor.manage but is NOT a superadmin.
      return this.can("distributor.manage") && this.cannot("superadmin.tenants");
    },

    // STORAGE / DISPLAY: still consumed by setLoadingVariable() to choose
    // which distributor record to load (dispatch on viewer's role, not a
    // permission gate). Migration of the underlying dispatch is deferred to
    // tier-2 (driven by a viewer.distributor_id field on the session).
    roleName() {
      let name;
      if (this.$store.state.account) {
        name = this.$store.state.account.role.name;
      }
      return name;
    },

    hasAccess() {
      // Edit a distributor: SA only (superadmin.tenants).
      // Create a distributor: SA or DM (both hold distributor.manage; SA also
      // holds superadmin.tenants).
      if (this.$route.params.id) {
        return this.can("superadmin.tenants");
      }
      return this.can("distributor.manage");
    },

    endAction() {
      return () => this.$router.push(`/admin/distributors/`);
    },

    distributorID() {
      let id;
      if (this.$store.state.account) {
        id = this.$store.state.account.distributor_id;
      }
      return id;
    },
  },

  mixins: [rules, helpers],

  async mounted() {
    this.$store.state.title = "Company";
    this.$store.state.topTitle = false;
    // Trigger the load when EITHER the Vuex roleName is populated OR the
    // Pinia account store carries an effective tenant id.
    //
    // SA impersonation reality: after `window.location.reload()` into the
    // impersonated context, requireToken's boot `accounts/:id` fetch 404s
    // (the SAFRS Account GET is _distributor_perms-scoped and the SA's own
    // distributor_id-NULL row is invisible to the impersonated tenant).
    // The guard tolerates that failure, hydrates Pinia from /auth/me, and
    // proceeds -- but the Vuex `login` action never runs, so
    // `$store.state.account` stays null forever, the `roleName` computed
    // returns undefined, and the previous `if (this.roleName)` gate
    // short-circuited the load. The `roleName` watcher also never fires
    // because roleName never changes from undefined.
    //
    // Adding the Pinia signal covers the SA-impersonation case without
    // changing behavior for the Owner path (where both signals are
    // populated; the load fires off the first one to arrive). It is a
    // STORAGE / DISPATCH change, not a permission gate -- capability
    // checks elsewhere remain authoritative.
    const piniaDistributorId = this.accountStore?.distributor?.id;
    if (this.roleName || piniaDistributorId != null) {
      this.setLoadingVariable(this.roleName);
      this.loadData();
    }
  },

  methods: {
    // CompanyForm never mutates the record; it asks us to.
    setField({ key, value }) {
      this.distributor[key] = value;
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async loadHomeState() {
      const location = JSON.parse(JSON.stringify(this.distributor.address));
      let response;
      try {
        response = await this.$axios.post(
          "/address_validation/parseAndValidateAddress",
          {
            location: location,
          }
        );
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        if (isUnparseableAddress(response.data)) {
          this.$store.dispatch("raiseError", unparseableAddressMessage(response.data));
        } else if (response.data.error) {
          this.$store.dispatch("raiseError", response.data.error);
          this.$refs.validateAddressDialog.showErrorDialog();
          // could not validate address
          // show couldn't validate popup
        } else {
          this.newDistributorHomeState = response.data.region;
        }
      }
    },

    updateCreateButtonPress() {
      if (this.addressChanged) {
        this.openValidateAddressDialog();
      } else {
        this.submitHandler(this.redirectToHome);
      }
    },

    updateCreateAddUsersButtonPress() {
      if (this.addressChanged) {
        this.openValidateAddressDialog();
      } else {
        this.submitHandler(this.redirectToAddAccounts);
      }
    },

    openValidateAddressDialog() {
      this.$refs.validateAddressDialog.show();
    },

    updateAddressToValidated(location) {
      this.distributor.address =
        location.street_and_number +
        ", " +
        (location.suite_unit_etc ? location.suite_unit_etc + ", " : "") +
        location.city +
        ", " +
        location.region +
        ", " +
        location.zip_code +
        ", " +
        location.country;
      // Remember the home state BEFORE submitting: the create branch of
      // submitHandler seeds it into nexus.
      this.newDistributorHomeState = location.region || null;
      this.submitHandler(this.redirectToHome);
    },

    async loadData() {
      // Guard against concurrent re-entry. The `roleName` watcher + the
      // mounted() hook can both fire during auth state hydration, each
      // calling loadData(). Without this guard, multiple in-flight calls
      // would each fire their own distributor fetch.
      if (this._loadDataInflight) {
        return this._loadDataInflight;
      }
      this._loadDataInflight = (async () => {
        try {
          this.loading = true;

          if (this.loadingVariable) {
            await this.loadDistributor();
            // await this.loadHomeState();
          } else {
            this.distributorPending = false;
          }
          this.loading = false;
        } finally {
          this._loadDataInflight = null;
        }
      })();
      return this._loadDataInflight;
    },

    // STORAGE / DISPATCH (NOT a permission gate):
    //
    // Dispatch order, most-specific to fallback:
    //   1. Pinia `account.distributor.id` -- the *effective* tenant for
    //      the current session. For an Owner this is their own tenant; for
    //      an SA actively impersonating, this is the impersonated tenant.
    //      Hydrated by `/auth/login`, `/auth/me`, and the impersonation
    //      start/stop endpoints.
    //   2. `$route.params.id` -- SA visiting a distributor by URL (e.g.,
    //      /admin/distributors/:id), no impersonation active.
    //   3. Vuex `account.distributor_id` -- legacy fallback for the Owner
    //      path when Pinia hasn't been hydrated (e.g., older callers not
    //      yet on the new login flow).
    //
    // This makes the function impersonation-aware: when an SA impersonates
    // a tenant and navigates to /distributor/account/company (no :id
    // segment; this page was at /distributor/profile before the 2026-08-11
    // route rename), the
    // Pinia store's distributor.id resolves to the impersonated tenant,
    // and the Company form loads the right data.
    setLoadingVariable(roleName) {
      const piniaDistributorId = this.accountStore?.distributor?.id;
      if (piniaDistributorId != null) {
        this.loadingVariable = piniaDistributorId;
      } else if (roleName == "Super Admin") {
        this.loadingVariable = this.$route.params.id;
      } else if (roleName == "Owner") {
        this.loadingVariable = this.distributorID;
      }

      // this.distributorPending = false;
    },

    // Non-UI create step: a brand-new distributor starts with its home
    // state in nexus. Never runs for an update, so a profile save cannot
    // overwrite what Account > Compliance saved.
    async seedHomeStateNexus(distributorID) {
      if (!this.newDistributorHomeState) {
        return;
      }
      try {
        await this.$axios.post(
          `/distributors/${distributorID}/edit_nexus_states`,
          {
            meta: {
              method: "edit_nexus_states",
              args: {
                nexus_states: [nexusStateName(this.newDistributorHomeState)],
              },
            },
          }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      }
    },

    async loadDistributor() {
      this.distributorPending = true;

      try {
        // jsonapi-vuex's `get` action requires a config object as the second
        // array element. `unpackArgs` returns the array verbatim, so a
        // single-element array leaves `config` undefined and the action's
        // `config['data'] = config['data'] || {}` throws
        // `TypeError: Cannot read properties of undefined (reading 'data')`
        // BEFORE the XHR fires -- the catch below then swallows it into
        // raiseError and the form binds blank. Always pass a config.
        const distributor = await this.$store.dispatch("jv/get", [
          `/distributors/${this.loadingVariable}`,
          {},
        ]);

        this.distributor = distributor;
      } catch (error) {
        // NOTE: notify via raiseError (Vuex snackbar) only. The previous
        // `this.$toasted.show(...)` calls crashed at runtime -- vue-toasted is
        // not installed or registered in this Vue 3 app, so `this.$toasted` is
        // undefined and `.show` throws. raiseError already surfaces the error;
        // the 404 branch keeps its redirect.
        if (error.response && error.response.status === 404) {
          this.$store.dispatch(
            "raiseError",
            `Distributor with ID ${this.loadingVariable} not found. Redirecting to distributor list.`
          );
          // Redirect to distributors list after a short delay
          setTimeout(() => {
            this.$router.push('/distributors');
          }, 1500);
        } else {
          this.$store.dispatch(
            "raiseError",
            "An error occurred while loading the distributor. Please try again."
          );
        }
      } finally {
        this.distributorPending = false;
      }
    },

    // Persist the record: ONE request for everything the form edits (the
    // time zone included), so a rejected value leaves nothing half-written.
    // The staged tax ID is applied by CompanyForm AFTER this resolves ok.
    // Resolves { ok: true } or { ok: false, error }; never throws.
    // Use _jv.id to detect update vs create -- route params may not exist
    // (the Owner's company route has no :id param).
    async persistRecord() {
      const updating = !!(this.distributor._jv && this.distributor._jv.id);
      try {
        if (updating) {
          // The form keeps saving as the owner types, so the owner may have
          // changed a field while this request was in flight: adopt the
          // server's tidied values only where the field is still what we
          // sent, and never overwrite a newer edit with an older response.
          const sent = { ...this.distributor };
          const saved = await this.$store.dispatch("jv/patch", this.distributor);
          this.adoptSaved(sent, saved);
        } else {
          this.distributor = await this.$store.dispatch("jv/post", [
            this.distributor,
            { url: "/distributors" },
          ]);
          await this.seedHomeStateNexus(this.distributor._jv.id);
        }
        this.savedVerb = updating ? "updated" : "created";
        return { ok: true };
      } catch (error) {
        // Never echo the server body or the field values into the message.
        if (error && error.response && error.response.status === 404) {
          setTimeout(() => {
            this.$router.push("/distributors");
          }, 1500);
          return {
            ok: false,
            error:
              "The distributor you're trying to update no longer exists. Redirecting to distributor list.",
          };
        }
        const status = error && error.response && error.response.status;
        // A canceled subscription makes the account read-only: the API
        // refuses every write with its own plain-words reason (no field
        // values in it). Show that, not "you do not have permission".
        const body = (error && error.response && error.response.data) || {};
        if (
          status === 403 &&
          body.error === "subscription_inactive" &&
          typeof body.message === "string" &&
          body.message
        ) {
          return { ok: false, error: body.message };
        }
        // A bad ZIP is the one refusal worth naming (the form saves as the
        // owner types). Only the plain-words rule is shown: the server's text
        // names a column and is never echoed.
        const serverText = JSON.stringify((body && body.errors) || "");
        if (status === 400 && /ZIP code must be 5 digits/i.test(serverText)) {
          return {
            ok: false,
            error:
              "The company details were not saved. The ZIP code must be 5 digits (or ZIP+4), e.g. 90210.",
          };
        }
        const detail =
          status === 403
            ? " You do not have permission to change one of these settings."
            : status === 400
              ? " One of the values is not valid."
              : "";
        return {
          ok: false,
          error: `The company details were not saved.${detail} Please try again.`,
        };
      }
    },

    // The Company form's autosave (Bug Intake #418). It only ever UPDATES the
    // loaded record: a first keystroke must never create a distributor.
    async saveRecord() {
      if (!(this.distributor._jv && this.distributor._jv.id)) {
        return {
          ok: false,
          error: "This company has not been created yet, so nothing can be saved here.",
        };
      }
      return this.persistRecord();
    },

    // Merge a save's response into the record the form is editing, field by
    // field: only fields the owner has not touched since the request left.
    adoptSaved(sent, saved) {
      if (!saved) return;
      for (const key of [...COMPANY_FIELDS, "address"]) {
        if (this.distributor[key] === sent[key] && key in saved) {
          this.distributor[key] = saved[key];
        }
      }
    },

    // Legacy entry (address-validation dialog): save, then finalize.
    async submitHandler(callback) {
      const result = await this.persistRecord();
      if (!result.ok) {
        this.$store.dispatch("raiseError", result.error);
        return;
      }
      this.finalizeSave(this.savedVerb || "updated", callback);
    },

    // Success-side finalization after a distributor create/update + nexus
    // save. Previously this lived inside updateProductTypes(); the product-
    // type binding was removed (clean break) but the save flow still owns
    // the success toast, navigation, and the caller callback.
    finalizeSave(actionVerb, callback) {
      this.$store.dispatch(
        "raiseSuccess",
        // Name the distributor; never show its numeric id.
        `Distributor ${
          (this.distributor.name || "").trim()
            ? (this.distributor.name || "").trim() + " "
            : ""
        }${actionVerb}`
      );
      this.endAction();
      typeof callback === "function" && callback();
    },

    redirectToHome() {
      this.$router.push(`/admin`);
    },

    // "Save and add users" -> the distributor's OWN Team & Permissions
    // invite flow.
    //
    // This page is Owner-reachable (/distributor/account/profile, gated
    // distributor.account.read). It used to push /admin/accounts/add,
    // which now redirects to /admin/users/new -- an SA surface gated on
    // superadmin.settings. Owners are seeded as "every capability MINUS
    // superadmin.*", so they held the old account.create and do NOT hold
    // superadmin.settings: the button would silently bounce them to
    // /admin. The SA Users page stays SA-only; an Owner adds users
    // through the surface that belongs to their own tenant instead.
    //
    // No distributor_id param: the invite route acts on the caller's own
    // distributor, resolved server-side from the session. Passing a
    // tenant id into a route that does not read one would be misleading
    // at best and a cross-tenant handle at worst.
    redirectToAddAccounts() {
      this.$router.push("/distributor/account/team/invite");
    },
  },
};
</script>
